import { Filter, MapPin, Check, X } from 'lucide-react';
import { DifficultyFilter, SkillFilter, RideTypeFilter, TimeFilter, MultiDayFilter } from '@/lib/types/core';
import { Point } from '@/lib/types/core';
import { getBestMatch } from '@/lib/geocoding';
import { useState, useEffect, useRef } from 'react';
import { TerrainFilter } from '@/components/terrain';
import { TerrainType } from '@/lib/types/core/terrain';

interface RideFiltersPanelProps {
  timeFilter: TimeFilter;
  setTimeFilter: (filter: TimeFilter) => void;
  difficultyFilter: DifficultyFilter;
  setDifficultyFilter: (filter: DifficultyFilter) => void;
  skillFilter: SkillFilter;
  setSkillFilter: (filter: SkillFilter) => void;
  rideTypeFilter: RideTypeFilter;
  setRideTypeFilter: (filter: RideTypeFilter) => void;
  multiDayFilter: MultiDayFilter;
  setMultiDayFilter: (filter: MultiDayFilter) => void;
  terrainFilter: TerrainType[];
  setTerrainFilter: (filter: TerrainType[]) => void;
  setRideFilterLocation: (location: Point) => void;
}

const RideFiltersPanel = ({
  timeFilter,
  setTimeFilter,
  difficultyFilter,
  setDifficultyFilter,
  skillFilter,
  setSkillFilter,
  rideTypeFilter,
  setRideTypeFilter,
  multiDayFilter,
  setMultiDayFilter,
  terrainFilter,
  setTerrainFilter,
  setRideFilterLocation,
}: RideFiltersPanelProps) => {
  const [addressInput, setAddressInput] = useState('');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingStatus, setGeocodingStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (addressInput.trim()) {
      debounceTimeoutRef.current = setTimeout(() => {
        handleAddressGeocode();
      }, 1000);
    } else {
      setGeocodingStatus('idle');
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [addressInput]);

  const handleAddressGeocode = async () => {
    if (!addressInput.trim()) return;

    setIsGeocoding(true);
    setGeocodingStatus('idle');

    try {
      const result = await getBestMatch(addressInput.trim());
      
      if (result) {
        const point: Point = {
          latitude: result.coordinates[1],
          longitude: result.coordinates[0]
        };
        setRideFilterLocation(point);
        setGeocodingStatus('success');
      } else {
        setGeocodingStatus('error');
      }
    } catch (error) {
      setGeocodingStatus('error');
      console.error('Geocoding error:', error);
    } finally {
      setIsGeocoding(false);
    }
  };

  const getStatusIcon = () => {
    if (isGeocoding) {
      return <div className="w-5 h-5 border-2 border-adv-orange border-t-transparent rounded-full animate-spin" />;
    }
    
    switch (geocodingStatus) {
      case 'success':
        return <Check className="text-green-400" size={20} />;
      case 'error':
        return <X className="text-red-400" size={20} />;
      default:
        return null;
    }
  };

  return (
    <div className="glass-card rounded-lg p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">Filters</h3>
        <Filter size={18} className="text-adv-orange" />
      </div>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Location Filter
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-adv-lightGray" size={16} />
            <input
              type="text"
              placeholder="Enter address to filter rides by location..."
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              className="w-full pl-10 pr-12 py-2 bg-adv-dark border border-adv-darkGray rounded-md text-white placeholder-adv-lightGray focus:outline-none focus:ring-2 focus:ring-adv-orange focus:border-transparent"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {getStatusIcon()}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Timeframe
          </label>
          <div className="space-y-2">
            {(['All', 'Today', 'This Week', 'This Month', 'Future'] as const).map((time) => (
              <div key={time} className="flex items-center">
                <input
                  type="radio"
                  id={`time-${time}`}
                  name="time"
                  checked={timeFilter === time}
                  onChange={(e) => {
                    e.preventDefault();
                    setTimeFilter(time);
                  }}
                  className="h-4 w-4 text-adv-orange focus:ring-adv-orange border-adv-darkGray bg-adv-dark"
                />
                <label 
                  htmlFor={`time-${time}`} 
                  className="ml-2 text-sm text-adv-lightGray cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  {time}
                </label>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            Terrain Difficulty
          </label>
          <div className="space-y-2">
            {(['All', 'Easy', 'Moderate', 'Hard'] as const).map((difficulty) => (
              <div key={difficulty} className="flex items-center">
                <input
                  type="radio"
                  id={`difficulty-${difficulty}`}
                  name="difficulty"
                  checked={difficultyFilter === difficulty}
                  onChange={(e) => {
                    e.preventDefault();
                    setDifficultyFilter(difficulty);
                  }}
                  className="h-4 w-4 text-adv-orange focus:ring-adv-orange border-adv-darkGray bg-adv-dark"
                />
                <label 
                  htmlFor={`difficulty-${difficulty}`} 
                  className="ml-2 text-sm text-adv-lightGray cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  {difficulty}
                </label>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            Rider Skill Level
          </label>
          <div className="space-y-2">
            {(['All', 'Beginner', 'Intermediate', 'Advanced'] as const).map((skill) => (
              <div key={skill} className="flex items-center">
                <input
                  type="radio"
                  id={`skill-${skill}`}
                  name="skill"
                  checked={skillFilter === skill}
                  onChange={(e) => {
                    e.preventDefault();
                    setSkillFilter(skill);
                  }}
                  className="h-4 w-4 text-adv-orange focus:ring-adv-orange border-adv-darkGray bg-adv-dark"
                />
                <label 
                  htmlFor={`skill-${skill}`} 
                  className="ml-2 text-sm text-adv-lightGray cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  {skill}
                </label>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            Ride Type
          </label>
          <div className="space-y-2">
            {(['All', 'Street', 'Seeking Off-road', 'Fully Off-road'] as const).map((type) => (
              <div key={type} className="flex items-center">
                <input
                  type="radio"
                  id={`type-${type.replace(/\s+/g, '-').toLowerCase()}`}
                  name="type"
                  checked={rideTypeFilter === type}
                  onChange={(e) => {
                    e.preventDefault();
                    setRideTypeFilter(type);
                  }}
                  className="h-4 w-4 text-adv-orange focus:ring-adv-orange border-adv-darkGray bg-adv-dark"
                />
                <label 
                  htmlFor={`type-${type.replace(/\s+/g, '-').toLowerCase()}`} 
                  className="ml-2 text-sm text-adv-lightGray cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  {type}
                </label>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            Duration
          </label>
          <div className="space-y-2">
            {(['All', 'Single-day', 'Multi-day'] as const).map((duration) => (
              <div key={duration} className="flex items-center">
                <input
                  type="radio"
                  id={`duration-${duration.replace(/\s+/g, '-').toLowerCase()}`}
                  name="duration"
                  checked={multiDayFilter === duration}
                  onChange={(e) => {
                    e.preventDefault();
                    setMultiDayFilter(duration);
                  }}
                  className="h-4 w-4 text-adv-orange focus:ring-adv-orange border-adv-darkGray bg-adv-dark"
                />
                <label
                  htmlFor={`duration-${duration.replace(/\s+/g, '-').toLowerCase()}`}
                  className="ml-2 text-sm text-adv-lightGray cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  {duration}
                </label>
              </div>
            ))}
          </div>
        </div>

        <TerrainFilter selected={terrainFilter} onChange={setTerrainFilter} />
      </div>
    </div>
  );
};

export default RideFiltersPanel;
